import type { AuditSeverity } from "../types";

const MODULE_LABELS: Record<string, string> = {
  user_management: "User Management",
  role_management: "Role Management",
  module_manager: "Module Manager",
  dropdown_manager: "Dropdown Manager",
  audit_center: "Audit Center",
  finance_accounts: "Accounts",
  auth: "Authentication",
  audit: "Audit Center",
};

const ENTITY_LABELS: Record<string, string> = {
  user: "User",
  role: "Role",
  user_role: "Role Assignment",
  module_config: "Module",
  dropdown_config: "Dropdown",
  audit_log: "Audit Log",
  role_permission: "Role Permission",
  meeting_attendee: "Meeting Attendee",
  company: "Company",
  partner: "Partner",
  partner_ownership: "Partner Ownership",
  finance_account: "Account",
  finance_entry: "Account Entry",
  product: "Product",
  project: "Project",
  task: "Task",
  action: "Action",
  note: "Note",
  meeting: "Meeting",
  budget: "Budget",
  expense: "Expense",
  idea: "Idea",
  decision: "Decision",
  milestone: "Milestone",
  system: "System",
  config: "Config",
};

export function formatModule(value: string | null | undefined): string {
  if (!value) return "—";
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "unknown") return "—";
  const key = trimmed.toLowerCase();
  if (MODULE_LABELS[key]) return MODULE_LABELS[key]!;
  return key
    .split("_")
    .map((s) => (s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : ""))
    .join(" ");
}

export function formatEntityType(value: string | null | undefined): string {
  if (!value) return "—";
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "unknown") return "—";
  const key = trimmed.toLowerCase();
  if (ENTITY_LABELS[key]) return ENTITY_LABELS[key]!;
  return key
    .split("_")
    .map((s) => (s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : ""))
    .join(" ");
}

export function formatIp(ip: string | null | undefined): string {
  if (!ip) return "—";
  if (ip === "::1" || ip === "::ffff:127.0.0.1" || ip === "127.0.0.1") {
    return "localhost";
  }
  return ip.replace(/^::ffff:/, "");
}

export function initialsOf(name: string | null | undefined): string {
  if (!name) return "SY";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "SY";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${date}, ${time}`;
}

export function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diffSec = Math.floor((Date.now() - d) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  const months = Math.floor(diffDay / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  const years = Math.floor(diffDay / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

export function truncate(value: string, max = 60): string {
  if (value.length <= max) return value;
  return value.slice(0, max - 1) + "…";
}

const ACTION_DOT: Record<string, string> = {
  CREATE: "hsl(142,72%,45%)",
  UPDATE: "hsl(210,100%,50%)",
  FIELD_CHANGED: "hsl(210,100%,50%)",
  DELETE: "hsl(0,72%,50%)",
  LOGIN: "hsl(173,72%,40%)",
  LOGOUT: "hsl(173,72%,40%)",
  LOGIN_FAILED: "hsl(38,92%,50%)",
  EXPORT: "hsl(262,72%,55%)",
  PERMISSION_CHANGED: "hsl(38,92%,50%)",
  ROLE_CHANGED: "hsl(262,72%,55%)",
};

export function actionDotColor(action: string): string {
  return ACTION_DOT[action] ?? "hsl(215,16%,60%)";
}

export const SEVERITY_DOT_COLOR: Record<AuditSeverity, string> = {
  INFO: "hsl(210,100%,50%)",
  WARNING: "hsl(38,92%,50%)",
  ERROR: "hsl(0,72%,50%)",
  CRITICAL: "hsl(0,72%,32%)",
};

export const SEVERITY_LABEL: Record<AuditSeverity, string> = {
  INFO: "Info",
  WARNING: "Warning",
  ERROR: "Error",
  CRITICAL: "Critical",
};
